/**
 * Task-management tour (Figma 9607-20688): shown once, on the first task page
 * that has evaluation results, and replayable from the task header's "View
 * Guided Tour" button. Opens with a centered welcome bubble, then five marks
 * on the Evaluations tab.
 *
 * All steps live on the single `task` page. None has a completion predicate:
 * Next (or "Got it" on the last one) moves on, X / Esc dismisses the tour.
 * Context comes from the components that render the marked controls, each
 * owning its own slice (`useWalkthroughContextValue` clears a slice when its
 * publisher unmounts, so two components must not share one):
 * - `ctx.task` from the task page header: `{ hasPlayground, hasActions,
 *   hasPrimaryAction }` — a mark whose control the task does not have is left
 *   out of the count;
 * - `ctx.evaluationTools` from the Evaluations FilterBar: `{ hasTools }`. The
 *   tools mark applies only while that bar is mounted with Adjust Threshold
 *   or Improve on it; the pointer passes over it otherwise and comes back
 *   (deferred-step revisit) should the bar show up while the tour is still on.
 */
import { notchedUnionRects } from '../utils/anchorRects';

export const TASK_MANAGEMENT_WALKTHROUGH_ID = 'task_management';

/** The one "page" of the task tour, for the runner's `currentPage` / `pageOrder`. */
export const TASK_MANAGEMENT_PAGE = 'task';
export const TASK_MANAGEMENT_PAGE_ORDER = Object.freeze([TASK_MANAGEMENT_PAGE]);

export const TASK_MANAGEMENT_WELCOME_CTA = 'Show me around';

/** The kebab's open options panel (TaskKebabMenu); absolutely positioned under the button. */
export const TASK_ACTIONS_PANEL_SELECTOR = '.task-page-header__kebab-panel';
const TASK_ACTIONS_BUTTON_SELECTOR = 'button[aria-haspopup="menu"]';

const present = (key) => (ctx) => ctx.task?.[key] !== false;

/** Only once the FilterBar said so: no bar on screen means nothing to point at. */
const hasEvaluationTools = (ctx) => ctx.evaluationTools?.hasTools === true;

export const TASK_MANAGEMENT_WALKTHROUGH_STEPS = [
    {
        id: 'tm-welcome',
        wizardStep: TASK_MANAGEMENT_PAGE,
        // Centered under the navbar, no anchor and no counter.
        centered: true,
        counted: false,
        title: 'Welcome to Task Management!',
        body: "Let's take a quick tour to get you oriented. Click 'Next' to learn how this page works and exactly where to find everything you need.",
        cta: TASK_MANAGEMENT_WELCOME_CTA,
    },
    {
        id: 'tm-evaluation',
        wizardStep: TASK_MANAGEMENT_PAGE,
        target: 'evaluation-panel',
        title: 'Track quality & performance',
        body: 'In Evaluation page you can see how your model performs against baselines teacher model. Check your quality metrics, identify weak spots, and take action to improve accuracy.',
        placement: 'top',
        align: 'start',
    },
    {
        id: 'tm-tools',
        wizardStep: TASK_MANAGEMENT_PAGE,
        target: 'evaluation-tools',
        title: 'Tools to improve accuracy',
        body: 'Use "Adjust Threshold" & "Improve" tools to improve your results. Adjust your confidence thresholds, or use the Improve tool to manually review and approve classifications so the model learns from your feedback.',
        placement: 'bottom',
        align: 'end',
        isApplicable: hasEvaluationTools,
    },
    {
        id: 'tm-playground',
        wizardStep: TASK_MANAGEMENT_PAGE,
        target: 'task-playground',
        title: 'Test in the Playground',
        body: "Experiment with different system & user prompts Run them side-by-side to instantly see how your prompt tweaks affect the model's generated output.",
        placement: 'bottom',
        align: 'end',
        isApplicable: present('hasPlayground'),
    },
    {
        id: 'tm-actions',
        wizardStep: TASK_MANAGEMENT_PAGE,
        target: 'task-actions',
        title: 'Need to make changes?',
        body: 'You can find all the actions you need here',
        // The kebab menu opens itself while this mark is up (TaskKebabMenu).
        // One outline around the button and its open options, bubble to the
        // left of both so the panel never covers it and the options stay
        // readable. The wrapper's own box is only the button: the panel is
        // absolutely positioned, so it is measured separately and observed
        // for its mount.
        placement: 'left',
        anchorRect: notchedUnionRects(TASK_ACTIONS_BUTTON_SELECTOR, TASK_ACTIONS_PANEL_SELECTOR),
        observe: [TASK_ACTIONS_PANEL_SELECTOR],
        isApplicable: present('hasActions'),
    },
    {
        id: 'tm-deploy',
        wizardStep: TASK_MANAGEMENT_PAGE,
        target: 'task-primary-action',
        title: 'Go live in one click',
        body: "Once you're satisfied with your performance metrics, hit 'Deploy' to integrate your custom model directly into your stack.",
        placement: 'bottom',
        align: 'end',
        isApplicable: present('hasPrimaryAction'),
    },
];

export const taskManagementWalkthrough = {
    id: TASK_MANAGEMENT_WALKTHROUGH_ID,
    style: 'guide',
    steps: TASK_MANAGEMENT_WALKTHROUGH_STEPS,
    requiredContext: ['task'],
};

export default taskManagementWalkthrough;
